import { EllipsisHorizontalIcon } from "@heroicons/react/16/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Editor } from "@monaco-editor/react";
import { ReactElement, useEffect, useState } from "react";
import { Controller, FieldArrayWithId, useFormContext } from "react-hook-form";

import { processLiquidTemplate } from "../services/integrations/process-liquid-template.ts";
import { HasEntityMappings } from "../types/form-inputs.ts";
import {
  prettifyJson,
  validateArrayOfStrings,
} from "../utils/json-validation.ts";
import { Checkbox } from "./catalyst/Checkbox.tsx";
import { CheckboxField } from "./catalyst/CheckboxField.tsx";
import { Dropdown } from "./catalyst/Dropdown.tsx";
import { DropdownButton } from "./catalyst/DropdownButton.tsx";
import { DropdownItem } from "./catalyst/DropdownItem.tsx";
import { DropdownMenu } from "./catalyst/DropdownMenu.tsx";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import { Field } from "./catalyst/Field.tsx";
import { Input } from "./catalyst/Input.tsx";
import { Label } from "./catalyst/Label.tsx";
import { TableCell } from "./catalyst/TableCell.tsx";
import { ExpandableTableRow } from "./ExpandableTableRow.tsx";
import EntitySelectField from "./inputs/EntitySelectField.tsx";
import MonacoLiquidEditor from "./json/MonacoLiquidEditor.tsx";
import { LiquidTemplateAiButton } from "./LiquidTemplateAiButton.tsx";
import { Tooltip } from "./Tooltip.tsx";

interface EntityMappingRowProps {
  entity: FieldArrayWithId<HasEntityMappings, "entityMappings", "id">;
  index: number;
  openDialog?: (index?: null | number) => void;
  remove?: (index: number) => void;
}
const EntityMappingRow = ({
  entity,
  index,
  openDialog,
  remove,
}: EntityMappingRowProps): ReactElement => {
  const {
    control,
    formState: { errors },
    getFieldState,
    getValues,
    register,
    setError,
    setValue,
    watch,
  } = useFormContext<HasEntityMappings>();

  const [editorHeight, setEditorHeight] = useState(40);

  const handleRowToggle = (isExpanded: boolean): void => {
    setEditorHeight(isExpanded ? 300 : 40);
  };

  const jsonPayload = watch("jsonResponse");
  const outputIsJson = watch(`entityMappings.${index}.outputIsJson`);

  const processTemplate = (templateValue: string | undefined): void => {
    const finalTemplate = templateValue || "";
    const handleProcessTemplate = processLiquidTemplate(
      finalTemplate,
      (value) => {
        const formatted = outputIsJson ? prettifyJson(value) : value;
        setValue(`entityMappings.${index}.value`, formatted);
      },
      (errorMessage) => {
        setError(`entityMappings.${index}.value`, {
          message: errorMessage,
        });
      },
      jsonPayload,
    );
    handleProcessTemplate();
  };

  const handleTemplateReturn = (template: string): void => {
    setValue(`entityMappings.${index}.liquidTemplate`, template);
    processTemplate(template);
  };

  const handleEditorChange = (
    onChange: (value: string) => void,
    templateValue: string | undefined,
  ): void => {
    onChange(templateValue || "");
    processTemplate(templateValue);
  };

  useEffect(() => {
    const templateValue = getValues(`entityMappings.${index}.liquidTemplate`);
    if (jsonPayload !== "" && templateValue !== "") {
      processTemplate(templateValue);
    }
  }, [jsonPayload]);

  useEffect(() => {
    if (outputIsJson) {
      const currentValue = getValues(`entityMappings.${index}.value`);
      setValue(`entityMappings.${index}.value`, prettifyJson(currentValue), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [outputIsJson]);

  const relPath = `entityMappings.${index}.relationship` as const;

  return (
    <ExpandableTableRow key={index} onToggle={handleRowToggle}>
      <TableCell className="w-1/4 align-top">
        <EntitySelectField
          control={control}
          errors={errors}
          fieldName={`entityMappings.${index}.entityQualifiedName`}
          onChangeEntity={(entity) => {
            if (!entity) return;

            const { isDirty } = getFieldState(relPath);
            if (isDirty) return; // user already set/changed it

            // if the old value is persisted, can't change it automatically
            const isPersisted = getValues(
              `entityMappings.${index}.isPersisted`,
            );
            const currentValue = getValues(relPath);
            if (isPersisted && currentValue) return;

            setValue(relPath, `to${entity.name.replace(/\s+/g, "")}`, {
              shouldDirty: false,
              shouldTouch: false,
            });
          }}
          placeHolder="Enter entity"
          rules={{
            required: "Output entity type is required",
          }}
        />
      </TableCell>

      <TableCell className="w-1/4 align-top">
        <div className="flex items-start gap-2">
          <Field className="min-w-0 flex-1">
            <Controller
              control={control}
              defaultValue=""
              name={`entityMappings.${index}.liquidTemplate`}
              render={({ field: { onChange, value } }) => (
                <div
                  style={{
                    height: `${editorHeight}px`,
                    transition: "height 0.3s ease-in-out",
                  }}
                >
                  <MonacoLiquidEditor
                    onChange={(templateValue) =>
                      handleEditorChange(onChange, templateValue)
                    }
                    value={value || ""}
                    width="300px"
                  />
                </div>
              )}
              rules={{
                required: "The liquid template is required",
              }}
            />
            {errors?.entityMappings?.[index]?.liquidTemplate && (
              <ErrorMessage>
                {errors.entityMappings[index].liquidTemplate.message}
              </ErrorMessage>
            )}
          </Field>
          <LiquidTemplateAiButton
            jsonContext={jsonPayload ?? ""}
            onSuccess={handleTemplateReturn}
          />
        </div>
      </TableCell>

      <TableCell className="w-1/4 align-top">
        <Field>
          <Controller
            control={control}
            defaultValue=""
            name={`entityMappings.${index}.value`}
            render={({ field: { onChange, value } }) => (
              <div
                style={{
                  height: `${editorHeight}px`,
                  transition: "height 0.3s ease-in-out",
                }}
              >
                <Editor
                  className="monaco-bordered"
                  height="100%"
                  language={outputIsJson ? "json" : "plaintext"}
                  onChange={(templateValue) =>
                    handleEditorChange(onChange, templateValue)
                  }
                  options={{
                    automaticLayout: true,
                    lineNumbers: "off",
                    minimap: { enabled: false },
                    readOnly: true,
                    wordWrap: "off",
                  }}
                  theme="miru-dark"
                  value={value}
                  width="100%"
                />
              </div>
            )}
            rules={{
              validate: (value) => {
                if (value && outputIsJson) {
                  try {
                    const parsed = JSON.parse(value);
                    const valid = validateArrayOfStrings(parsed);
                    if (!valid) {
                      return "JSON is not array of strings";
                    }
                    return true;
                  } catch (error) {
                    return `Invalid JSON ${error}`;
                  }
                } else {
                  return true;
                }
              },
            }}
          />
          {errors?.entityMappings?.[index]?.value && (
            <ErrorMessage className="block max-w-[275px] whitespace-pre-wrap break-words text-sm">
              {errors.entityMappings[index].value.message}
            </ErrorMessage>
          )}
        </Field>

        <Controller
          control={control}
          name={`entityMappings.${index}.outputIsJson`}
          render={({ field }) => (
            <CheckboxField className="mb-2 mt-2">
              <Checkbox
                checked={field.value}
                className="scale-75"
                onChange={field.onChange}
              />
              <Label className="flex items-center" size="xs">
                Treat as List
                <span className="ml-1">
                  <Tooltip
                    content={`Expected format:
[
  "value1",
  ...
]`}
                    position="right"
                  >
                    <InformationCircleIcon className="h-3 w-3 text-gray-500 hover:text-gray-700" />
                  </Tooltip>
                </span>
              </Label>
            </CheckboxField>
          )}
        />
      </TableCell>
      <TableCell className="w-1/6 align-top">
        <Field>
          <Input
            {...register(`entityMappings.${index}.relationship`, {
              required: "The edge relationship is required",
            })}
            defaultValue={entity.value}
            invalid={!!errors?.entityMappings?.[index]?.relationship}
            placeholder="Edge Relationship"
          />
          {errors?.entityMappings?.[index]?.relationship && (
            <ErrorMessage>
              {errors.entityMappings[index].relationship.message}
            </ErrorMessage>
          )}
        </Field>
      </TableCell>
      <TableCell className="w-10 align-top">
        <Controller
          control={control}
          defaultValue={false}
          name={`entityMappings.${index}.isRoot`}
          render={({ field }) => (
            <div className="mt-2 flex justify-center">
              <Checkbox
                checked={field.value ?? false}
                className="scale-75"
                onChange={(e) => {
                  if (e) {
                    const items = getValues("entityMappings");

                    items.forEach((_, i) => {
                      setValue(`entityMappings.${i}.isRoot`, i === index, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    });
                  } else {
                    field.onChange(false);
                  }
                }}
              />
            </div>
          )}
        />
        {errors?.entityMappings?.[index]?.isRoot && (
          <ErrorMessage>
            {errors.entityMappings[index].isRoot.message}
          </ErrorMessage>
        )}
      </TableCell>
      <TableCell className="w-10 text-right align-top">
        {(remove || openDialog) && (
          <Dropdown>
            <DropdownButton aria-label="More options" plain>
              <EllipsisHorizontalIcon />
            </DropdownButton>
            <DropdownMenu>
              {remove && (
                <DropdownItem
                  className="hover:bg-red-500 hover:text-white"
                  onClick={() => remove(index)}
                >
                  Delete
                </DropdownItem>
              )}
              {openDialog && (
                <DropdownItem onClick={() => openDialog(index)}>
                  Advanced Dialog
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </TableCell>
    </ExpandableTableRow>
  );
};

export default EntityMappingRow;
